import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Fab,
  MenuItem,
  Paper,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import { useInView } from "react-intersection-observer";
import { useDebounce } from "use-debounce";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import { useTheme } from "@emotion/react";
import InvoiceTableRow from "./InvoiceTableRow";
import useInvoicedOrders from "../../../hooks/useInvoicedOrders";
import TextFieldDebounced from "../../../components/TextFieldDebounced";
import { Add } from "@mui/icons-material";
import CreateInvoiceDialog from "./CreateInvoiceDialog";

const Invoices = ({ search, status }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);

  const { authToken } = useContext(AuthContext);
  const [
    orders,
    onRefresh,
    refreshing,
    onLoadMore,
    loadingMore,
    total,
    hasMore,
  ] = useInvoicedOrders(authToken, 50, null, search, status);

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  const updateSearch = (searchUpdate, statusUpdate) => {
    let query = [];
    if (searchUpdate || statusUpdate) {
      if (searchUpdate) {
        query.push(`search=${searchUpdate}`);
      }
      if (statusUpdate) {
        query.push(`status=${statusUpdate}`);
      }
    }
    if (query.length > 0) {
      navigate(`/invoices?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/invoices`);
    }
  };

  useEffect(() => {
    if (orders.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <Typography variant="h4" component="div">
        Invoices
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ marginLeft: "8px", flex: 1 }}>
          <TextFieldDebounced
            fullWidth
            variant="outlined"
            label="Search"
            value={search}
            onChange={(e) => {
              updateSearch(e.target.value, status);
            }}
          />
        </Box>
        <TextField
          sx={{ width: "200px", marginLeft: "8px" }}
          select
          label="Status"
          value={status}
          onChange={(e) => {
            updateSearch(search, e.target.value);
          }}
        >
          <MenuItem value={"unpaid"}>Unpaid</MenuItem>
          <MenuItem value={"paid"}>Paid</MenuItem>
          {/* <MenuItem value={"pending"}>Pending</MenuItem> */}
          <MenuItem value={"all"}>All</MenuItem>
        </TextField>
      </Box>
      <InfiniteScrollerTable
        TableContainerComponent={Paper}
        TableStyle={{ minWidth: "600px" }}
        TableHead={() => (
          <TableHead>
            <TableRow>
              <TableCell align="left">Order ID</TableCell>
              <TableCell align="left">Project</TableCell>
              <TableCell align="left">Customer</TableCell>
              <TableCell align="left">Credit Type</TableCell>
              <TableCell align="left">Payment Status</TableCell>
              <TableCell align="left">Date Approved</TableCell>
              <TableCell align="left">$</TableCell>
              {/* <TableCell align="left">Invoice Balance</TableCell> */}
              {/* <TableCell align="left">Invoice Due Date</TableCell> */}
            </TableRow>
          </TableHead>
        )}
        items={orders}
        renderRow={(item, index) => {
          return (
            <InvoiceTableRow
              key={item.id}
              order={item}
              onSelect={(order) => {
                navigate(`/invoices/${order.id}`);
              }}
            />
          );
        }}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        loading={refreshing || loadingMore}
        ListFooterComponent={() => {
          return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Fab
          onClick={() => {
            setOpenInvoiceDialog(true);
          }}
          variant="extended"
          color="success"
        >
          <Add /> Invoice
        </Fab>
      </div>
      <CreateInvoiceDialog
        open={openInvoiceDialog}
        onClose={() => {
          setOpenInvoiceDialog(false);
        }}
      />
    </Box>
  );
};
export default Invoices;
